import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { IState } from 'store';
import { IAuthState } from 'store/modules/auth/types';
import { TOGGLE_SATISFACTION_SURVEY } from 'utils/flags';
import { dismissSatisfactionSurvey, isSatisfactionSurveyDismissed } from 'utils/satisfactionSurveySession';

import { fetchPendingSurveys, submitSatisfactionSurvey } from './api';

export const SATISFACTION_SURVEY_TYPE = 'geral';

const GENERIC_SUBMIT_ERROR = 'Não foi possível enviar sua resposta. Tente novamente.';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildSubmitErrorMessage(error: any): string {
  const status = error?.response?.status;
  const message = error?.response?.data?.message;

  if (status === 409 && message) {
    return `Não foi possível enviar sua resposta. ${message}`;
  }

  return GENERIC_SUBMIT_ERROR;
}

export function useSatisfactionSurvey() {
  const { signed } = useSelector<IState, IAuthState>((state) => state.auth);

  const [pending, setPending] = useState<boolean>(false);
  const [dismissed, setDismissed] = useState<boolean>(isSatisfactionSurveyDismissed);
  const [sending, setSending] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!TOGGLE_SATISFACTION_SURVEY || !signed) return undefined;

    let active = true;

    fetchPendingSurveys()
      .then((pendingSurveys) => {
        if (active) {
          setPending(pendingSurveys.includes(SATISFACTION_SURVEY_TYPE));
        }
      })
      .catch(() => {
        setPending(false);
      });

    return () => {
      active = false;
    };
  }, [signed]);

  useEffect(() => {
    if (!submitted) return undefined;

    const timer = setTimeout(() => setDismissed(true), 4000);

    return () => clearTimeout(timer);
  }, [submitted]);

  const close = useCallback(() => {
    dismissSatisfactionSurvey();
    setDismissed(true);
  }, []);

  const submit = useCallback(async (rating: number, comentarios: string) => {
    setSending(true);
    setSubmitError(null);

    try {
      await submitSatisfactionSurvey({
        tipo: SATISFACTION_SURVEY_TYPE,
        rating,
        comentarios,
      });

      dismissSatisfactionSurvey();
      setSubmitted(true);
    } catch (error) {
      setSubmitError(buildSubmitErrorMessage(error));
    } finally {
      setSending(false);
    }
  }, []);

  return {
    visible: signed && pending && !dismissed,
    sending,
    submitted,
    submitError,
    close,
    submit,
  };
}
